import { documentToHtmlString, Options } from '@contentful/rich-text-html-renderer';
import { BLOCKS, INLINES, Document } from '@contentful/rich-text-types';
import { createClient, Entry, EntryCollection } from 'contentful';
import { from, Observable, throwError } from 'rxjs';
import { chain, extend, isEmpty, isUndefined } from 'lodash';
import { map } from 'rxjs/operators';
import { renderToString } from 'react-dom/server';
import * as React from 'react';
import { Asset } from '../asset/Asset';
import { flattenAssetFields, flattenHyperlinkFields, HyperlinkFields } from './assetTools';
import { Hyperlink } from '../asset/Hyperlink';

/*** types ***/

export interface BaseEntry {
    id: string;
    contentTypeId: string;
    createdAt: Date;
    updatedAt: Date;
}

export interface MenuItem extends BaseEntry {
    name: string;
    topLevel: boolean;
    isDefaultLocation: boolean;
    showAbbreviation: boolean;
    clickable: boolean;
    order: number;
    category: string;
    subMenuItems?: Array<MenuItem>;
}

type MenuItemCF = MenuItem & { subMenuItems?: Array<Entry<MenuItemCF>>, };

export interface Article extends BaseEntry {
    title: string;
    order: number;
    author?: string;
    body: string;
    rawBody: Document;
    belongsTo: MenuItem;
}

interface ArticleCF extends BaseEntry {
    title: string;
    order: number;
    author?: string;
    body: Document;
    belongsTo: Entry<MenuItemCF>;
}

export interface ContentfulClient {
    getMenuItems: () => Observable<Array<MenuItem>>;
    getArticles: (categories: Array<string>) => Observable<Array<Article>>;
}

const client = createClient({
    space: `${process.env.REACT_APP_SPACE}`,
    accessToken: `${process.env.REACT_APP_ACCESS_TOKEN}`,
    resolveLinks: true,
});

function convertToBaseEntry(entry: Entry<any>): BaseEntry {
    return {
        id: entry.sys.id,
        contentTypeId: entry.sys.contentType.sys.id,
        createdAt: new Date(entry.sys.createdAt),
        updatedAt: new Date(entry.sys.updatedAt),
    };
}

/*** getMenuItems ***/

function getMenuItems(): Observable<Array<MenuItem>> {
    return from(
        client.getEntries({
            content_type: 'menuItem',
            include: 2,
            'fields.topLevel': true,
        }))
        .pipe(map(convertToMenuItems));
}

const meuItemOrder = (item: MenuItem) => item.order;

function convertToMenuItems(data: EntryCollection<MenuItemCF>): Array<MenuItem> {
    if (data.errors) {
        throw new Error(data.errors.map(v => v.toString()).join(', '));
    }
    return chain(data.items.map(convertToMenuItem))
        .map(sortSubMenuItems)
        .orderBy(meuItemOrder)
        .value();
}

function convertToMenuItem(entry: Entry<MenuItemCF>): MenuItem {
    return {
        ...convertToBaseEntry(entry),
        name: entry.fields.name,
        topLevel: entry.fields.topLevel,
        isDefaultLocation: entry.fields.isDefaultLocation,
        showAbbreviation: entry.fields.showAbbreviation,
        clickable: entry.fields.clickable,
        order: entry.fields.order,
        category: entry.fields.category,
        subMenuItems: getSumMenuItems(entry.fields.subMenuItems),
    };
}

function getSumMenuItems(entries: Array<Entry<MenuItem>> | undefined): Array<MenuItem> {
    if (entries) {
        return entries.map(convertToMenuItem);
    } else {
        return [];
    }
}

function sortSubMenuItems(item: MenuItem): MenuItem {
    if (isUndefined(item.subMenuItems)) {
        return item;
    } else {
        const subMenuItems: Array<MenuItem> = chain(item.subMenuItems)
            .map(sortSubMenuItems)
            .orderBy(meuItemOrder)
            .value();
        return extend({}, item, {subMenuItems});
    }
}

/*** getArticles ***/

function getArticles(categories: Array<string>): Observable<Array<Article>> {
    if (isEmpty(categories)) {
        throwError('Empty categories not allowed in articles query to contentful');
    }
    return from(client.getEntries({
        content_type: 'article',
        include: 10,
        'fields.belongsTo.sys.contentType.sys.id': 'menuItem',
        'fields.belongsTo.fields.category[in]': `${categories.join(',')}`
    })).pipe(
        map(convertToArticles)
    );
}

function convertToArticles(entries: EntryCollection<ArticleCF>): Array<Article> {
    if (entries.errors) {
        throw new Error(entries.errors.map(v => v.toString()).join(', '));
    }
    return entries.items
        .map(convertToArticle)
        .sort((a1, a2) => a2.order - a1.order || a2.updatedAt.getTime() - a1.updatedAt.getTime());
}

const articleConversionOptions: Partial<Options> = {
    renderNode: {
        [BLOCKS.EMBEDDED_ASSET]: node => {
            return renderToString(
                React.createElement(Asset, flattenAssetFields(node.data.target.fields), null));
        },
        [INLINES.ASSET_HYPERLINK]: node => {
            return renderToString(
                React.createElement(Asset, flattenAssetFields(node.data.target.fields), null));
        },
        [INLINES.HYPERLINK]: node => {
            return renderToString(React.createElement(
                Hyperlink,
                flattenHyperlinkFields(node as unknown as HyperlinkFields), null)
            );
        },
    }
};

function convertToArticle(entry: Entry<ArticleCF>): Article {
    return {
        ...convertToBaseEntry(entry),
        title: entry.fields.title,
        order: entry.fields.order,
        author: entry.fields.author,
        rawBody: entry.fields.body,
        body: documentToHtmlString(entry.fields.body, articleConversionOptions),
        belongsTo: convertToMenuItem(entry.fields.belongsTo),

    };
}

/************************ */

export const contentfulClient: ContentfulClient = {
    getMenuItems,
    getArticles,
};


// WEBPACK FOOTER //
// ./src/contentful/contentfulClient.ts